import { Search, X } from 'lucide-react';

import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { cn } from '../../../components/ui/utils';
import { PLATFORM_CHIPS, type AccountsFilter, type PlatformChip, type StatusFilter } from '../lib/counts';

interface AccountsFilterBarProps {
  value: AccountsFilter;
  onChange: (next: AccountsFilter) => void;
  counts: { platform: Record<PlatformChip, number>; status: Record<StatusFilter, number> };
  /** Все тэги аккаунтов — чипами под поиском. */
  tags: string[];
}

const STATUS_LABEL: Record<StatusFilter, string> = { all: 'Любой статус', active: 'Активные', inactive: 'Неактивные' };

/**
 * Сеть — чипами (раньше TikTok был «статусом» рядом с «Активные»), статус —
 * отдельным списком, поиск и тэги — под ними. Всё в адресе страницы.
 */
export function AccountsFilterBar({ value, onChange, counts, tags }: AccountsFilterBarProps) {
  const set = (patch: Partial<AccountsFilter>) => onChange({ ...value, ...patch });
  // Сеть без единого аккаунта — без чипа: вечный «0» только мешает. Выбранный
  // чип остаётся, иначе из него не выйти.
  const chips = PLATFORM_CHIPS.filter((c) => c.value === 'all' || c.value === value.platform || counts.platform[c.value] > 0);

  const toggleTag = (tag: string) =>
    set({ tags: value.tags.includes(tag) ? value.tags.filter((t) => t !== tag) : [...value.tags, tag] });

  return (
    <div className="flex flex-col gap-3">
      {/* На узком экране чипы не помещаются — прокручиваем их, а не страницу */}
      <Tabs
        value={value.platform}
        onValueChange={(v) => set({ platform: v as PlatformChip })}
        className="-mx-1 max-w-full overflow-x-auto px-1"
      >
        <TabsList className="gap-1">
          {chips.map((chip) => (
            <TabsTrigger key={chip.value} value={chip.value} className="gap-2 px-4">
              <span>{chip.label}</span>
              {/* ponytail: bg-foreground/10 читается и на белом активном табе, и на сером треке */}
              <span className="min-w-5 rounded-full bg-foreground/10 px-1.5 text-xs font-semibold tabular-nums text-foreground">
                {counts.platform[chip.value]}
              </span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={value.query}
            onChange={(e) => set({ query: e.target.value })}
            placeholder="Поиск по нику или названию"
            className="pl-9"
            aria-label="Поиск аккаунта"
          />
        </div>
        <Select value={value.status} onValueChange={(v) => set({ status: v as StatusFilter })}>
          <SelectTrigger className="w-48" aria-label="Статус">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(STATUS_LABEL) as StatusFilter[]).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABEL[s]} · {counts.status[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {tags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground">Тэги:</span>
          {tags.map((tag) => {
            const active = value.tags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                aria-pressed={active}
                onClick={() => toggleTag(tag)}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
                  active ? 'border-primary bg-primary text-primary-foreground' : 'bg-background hover:bg-accent',
                )}
              >
                {tag}
              </button>
            );
          })}
          {value.tags.length > 0 && (
            <button
              type="button"
              onClick={() => set({ tags: [] })}
              className="inline-flex items-center gap-1 px-1.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <X className="size-3" />
              Сбросить
            </button>
          )}
        </div>
      )}
    </div>
  );
}
